import { useState, useEffect } from 'react';
import type { RenderedVideo } from '@/types/video-enhancer';

interface UseYouTubePublishParams {
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
}

export function useYouTubePublish({ toast }: UseYouTubePublishParams) {
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [youtubeChannel, setYoutubeChannel] = useState<{ name: string; thumbnailUrl?: string } | null>(null);
  const [showYoutubePublishModal, setShowYoutubePublishModal] = useState(false);
  const [youtubePublishData, setYoutubePublishData] = useState<{ videoUrl: string; name: string; thumbnailUrl?: string } | null>(null);
  const [youtubePublishForm, setYoutubePublishForm] = useState({ title: '', description: '', tags: '', privacy: 'private' });
  const [youtubeUploading, setYoutubeUploading] = useState(false);
  const [youtubeUploadResult, setYoutubeUploadResult] = useState<{ videoId: string; url: string } | null>(null);

  // Check YouTube connection status on mount
  useEffect(() => {
    const checkYoutubeStatus = async () => {
      try {
        const response = await fetch('/api/youtube/status', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setYoutubeConnected(data.connected);
          if (data.connected && data.channel) {
            setYoutubeChannel({ name: data.channel.title, thumbnailUrl: data.channel.thumbnailUrl });
          }
        }
      } catch (error) {
        console.error('Failed to check YouTube status:', error);
      }
    };
    checkYoutubeStatus();
  }, []);

  const connectYoutube = async () => {
    try {
      const response = await fetch('/api/youtube/auth', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank', 'width=600,height=700');
        const pollInterval = setInterval(async () => {
          const statusRes = await fetch('/api/youtube/status', { credentials: 'include' });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.connected) {
              clearInterval(pollInterval);
              setYoutubeConnected(true);
              if (statusData.channel) {
                setYoutubeChannel({ name: statusData.channel.title, thumbnailUrl: statusData.channel.thumbnailUrl });
              }
              toast({ title: 'YouTube Connected', description: `Connected to ${statusData.channel?.title || 'your channel'}` });
            }
          }
        }, 2000);
        setTimeout(() => clearInterval(pollInterval), 120000);
      }
    } catch (error) {
      console.error('Failed to start YouTube auth:', error);
      toast({ title: 'Connection Failed', description: 'Failed to connect to YouTube', variant: 'destructive' });
    }
  };

  const disconnectYoutube = async () => {
    try {
      const response = await fetch('/api/youtube/disconnect', { method: 'POST', credentials: 'include' });
      if (response.ok) {
        setYoutubeConnected(false);
        setYoutubeChannel(null);
        toast({ title: 'YouTube Disconnected', description: 'Your YouTube account has been disconnected' });
      }
    } catch (error) {
      console.error('Failed to disconnect YouTube:', error);
    }
  };

  const uploadToYoutube = async () => {
    if (!youtubePublishData) return;

    setYoutubeUploading(true);
    setYoutubeUploadResult(null);

    try {
      const response = await fetch('/api/youtube/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          videoUrl: youtubePublishData.videoUrl,
          title: youtubePublishForm.title || youtubePublishData.name,
          description: youtubePublishForm.description,
          tags: youtubePublishForm.tags.split(',').map(t => t.trim()).filter(Boolean),
          privacyStatus: youtubePublishForm.privacy,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setYoutubeUploadResult({ videoId: data.videoId, url: `https://youtube.com/watch?v=${data.videoId}` });
        toast({ title: 'Upload Complete', description: 'Video uploaded to YouTube successfully!' });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error('YouTube upload failed:', error);
      toast({ title: 'Upload Failed', description: error.message || 'Failed to upload video', variant: 'destructive' });
    } finally {
      setYoutubeUploading(false);
    }
  };

  const openYoutubePublishModal = (video: RenderedVideo) => {
    setYoutubePublishData({
      videoUrl: video.url || video.downloadUrl || video.videoUrl || '',
      name: video.name,
      thumbnailUrl: video.thumbnailUrl,
    });
    setYoutubePublishForm({
      title: video.name,
      description: '',
      tags: '',
      privacy: 'private',
    });
    setYoutubeUploadResult(null);
    setShowYoutubePublishModal(true);
  };

  return {
    youtubeConnected,
    youtubeChannel,
    showYoutubePublishModal,
    setShowYoutubePublishModal,
    youtubePublishData,
    youtubePublishForm,
    setYoutubePublishForm,
    youtubeUploading,
    youtubeUploadResult,
    connectYoutube,
    disconnectYoutube,
    uploadToYoutube,
    openYoutubePublishModal,
  };
}
